import React, { useState } from "react";

export default function TaskBoardLayout() {
  const [tasks, setTasks] = useState({
    nowLong: [],
    nowShort: [],
    thenLong: [],
    thenShort: [],
  });

  const [popup, setPopup] = useState({ open: false, section: null });
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newDueDate, setNewDueDate] = useState("");
  const [newFiles, setNewFiles] = useState([]);
  const [activeTask, setActiveTask] = useState(null);

  const handleNewTask = (section) => {
    setPopup({ open: true, section });
    setNewTitle("");
    setNewDescription("");
    setNewDueDate("");
    setNewFiles([]);
  };

  const confirmNewTask = () => {
    const newTask = {
      id: Date.now(),
      title: newTitle || "Neue Aufgabe",
      description: newDescription,
      dueDate: newDueDate,
      attachments: Array.from(newFiles),
      done: false,
      creator: "currentUser@example.com",
      notes: "",
      emailOn: null,
    };
    setTasks((prev) => ({
      ...prev,
      [popup.section]: [...prev[popup.section], newTask],
    }));
    setPopup({ open: false, section: null });
  };

  const toggleDone = (section, taskId) => {
    setTasks((prev) => {
      const currentTasks = [...prev[section]];
      const updatedTasks = currentTasks.map((task) => {
        if (task.id !== taskId) return task;
        const updatedTask = {
          ...task,
          done: !task.done,
          dueDate: task.done ? task.dueDate : "",
          emailOn: task.done ? task.emailOn : null,
        };
        return updatedTask;
      });
      const sortedTasks = [
        ...updatedTasks.filter((t) => !t.done),
        ...updatedTasks.filter((t) => t.done),
      ];
      return {
        ...prev,
        [section]: sortedTasks,
      };
    });
  };

  const showDetails = (task) => {
    setActiveTask(task);
  };

  const closeDetails = () => setActiveTask(null);

  const renderSection = (key, color, label, cornerClass) => (
    <div className={`relative overflow-hidden flex flex-col ${color}`}>
      <div
        className="absolute right-4 bottom-20 text-2xl italic text-[#1d4d4f]"
        style={{ writingMode: "vertical-rl", transform: "rotate(-180deg)" }}
      >
        {label}
      </div>

      <div className="flex-1 overflow-auto px-3 pt-5">
        {tasks[key].map((task) => (
          <div key={task.id} className="flex items-start text-white gap-2 mb-2">
            <input
              type="checkbox"
              checked={task.done}
              onChange={() => toggleDone(key, task.id)}
              className="mt-1"
            />
            <span
              className="cursor-pointer text-white"
              onClick={() => showDetails(task)}
            >
              ε
            </span>
            <span className={`select-none ${task.done ? "line-through opacity-70" : ""}`}>
              {task.title}
            </span>
          </div>
        ))}
      </div>

      <div
        onClick={() => handleNewTask(key)}
        className={`absolute w-8 h-8 m-1 cursor-pointer ${cornerClass} bg-black/30 hover:bg-black/50 transition`}
      ></div>
    </div>
  );

  return (
    <>
      <div className="w-screen h-screen grid grid-cols-2 grid-rows-2 gap-0">
        {renderSection("nowLong", "bg-[#276235]", "now – long", "bottom-0 right-0")}
        {renderSection("nowShort", "bg-[#7BB661]", "now – short", "bottom-0 right-0")}
        {renderSection("thenLong", "bg-[#5D8CA8]", "then – long", "bottom-0 right-0")}
        {renderSection("thenShort", "bg-[#B32821]", "then – short", "bottom-0 right-0")}
      </div>

      {/* Popup für Neue Aufgabe */}
      {popup.open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 shadow-xl space-y-4">
            <h2 className="text-xl font-bold">Neue Aufgabe</h2>

            <input
              type="text"
              placeholder="Titel eingeben"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
            />

            <textarea
              placeholder="Beschreibung eingeben"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
            ></textarea>

            <input
              type="date"
              value={newDueDate}
              onChange={(e) => setNewDueDate(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
            />

            <input
              type="file"
              multiple
              onChange={(e) => setNewFiles(e.target.files)}
              className="w-full"
            />

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setPopup({ open: false, section: null })}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded"
              >
                Abbrechen
              </button>
              <button
                onClick={confirmNewTask}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Hinzufügen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popup für Details */}
      {activeTask && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 shadow-xl space-y-4">
            <h2 className="text-xl font-bold mb-2">Aufgabendetails</h2>
            <div><strong>Titel:</strong> {activeTask.title}</div>
            <div><strong>Beschreibung:</strong> {activeTask.description || "(keine)"}</div>
            <div><strong>Fällig bis:</strong> {activeTask.dueDate || "(kein Datum)"}</div>
            {activeTask.attachments.length > 0 && (
              <div>
                <strong>Dateien:</strong>
                <ul className="list-disc list-inside text-sm">
                  {activeTask.attachments.map((file, i) => (
                    <li key={i}>{file.name}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex justify-end pt-2">
              <button
                onClick={closeDetails}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}